import { PersonalityType } from "../../__generated__/types";
import { STYLE_DESCRIPTIONS, STYLE_SLUG_OVERRIDES } from "./data";

export const normalizePersonalityKey = (value?: string | null) =>
  (value ?? "").replace(/\s+/g, "").toLowerCase();

export const normalizePersonalityEnum = (value?: string | null) =>
  value ? (value.replace(/\s+/g, "") as PersonalityType) : null;

export const toTitleCase = (value?: string | null) => {
  if (!value) return "";
  const normalized = value.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  return normalized
    .split(" ")
    .map((part) =>
      part
        .split("-")
        .map((segment) =>
          segment.length
            ? segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()
            : ""
        )
        .join("-")
    )
    .join(" ")
    .trim();
};

export const formatPersonalityType = (value?: string | null) => {
  if (!value) return "";
  const normalized = value.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.includes(" ")) {
    return toTitleCase(normalized);
  }
  const camelSpaced = normalized.replace(/([a-z])([A-Z])/g, "$1 $2").trim();
  if (camelSpaced.includes(" ")) {
    return toTitleCase(camelSpaced);
  }
  const lower = normalized.toLowerCase();
  const prefixOptions = ["extrovert", "introvert"];
  const colorOptions = ["blue", "orange", "green", "gold"];
  const tokens: string[] = [];
  let rest = lower;

  const prefix = prefixOptions.find((option) => rest.startsWith(option));
  if (prefix) {
    tokens.push(prefix);
    rest = rest.slice(prefix.length);
  }

  while (rest.length) {
    const match = colorOptions.find((option) => rest.startsWith(option));
    if (!match) break;
    tokens.push(match);
    rest = rest.slice(match.length);
  }

  if (tokens.length) {
    return tokens
      .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
      .join(" ");
  }

  return toTitleCase(normalized);
};

export const formatShortDate = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "";
  const numericValue =
    typeof value === "number"
      ? value
      : typeof value === "string" && /^\d+$/.test(value)
      ? Number(value)
      : value;
  const date = new Date(numericValue);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

export const formatCountLabel = (
  value: string | null | undefined,
  singular: string,
  plural: string
) => {
  if (!value) return "";
  const count = Number(value);
  if (Number.isNaN(count)) return value;
  return `${count} ${count === 1 ? singular : plural}`;
};

export const formatYesNo = (value: boolean | null | undefined) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Not provided";
};

export const formatKidsAges = (value: string | null | undefined) => {
  if (!value) return "";
  const ages = value
    .split(",")
    .map((age) => age.trim())
    .filter(Boolean);
  if (!ages.length) return "";
  return `Ages: ${ages.join(", ")}`;
};

export const formatPetLabel = (type: string, count?: string | null) => {
  const trimmedType = toTitleCase(type);
  const parsed = Number(count ?? "");
  if (!count || Number.isNaN(parsed)) {
    return trimmedType || "Pet";
  }
  if (!trimmedType) {
    return `${parsed} Pet${parsed === 1 ? "" : "s"}`;
  }
  return `${parsed} ${trimmedType}${parsed === 1 ? "" : "s"}`;
};

export const formatBudgetRangeLabel = (label: string) => {
  if (!label || label === "Not provided") return "Not provided";
  if (label.includes("$")) return label;
  return label.replace(/(\d[\d,]*)/g, (match) => `$${match}`);
};

export const formatSquareFootageLabel = (value: string | null | undefined) => {
  if (!value) return "";
  return toTitleCase(value)
    .replace(/Square Feet/i, "sqft")
    .replace(/Square Foot/i, "sqft");
};

export const formatValue = (value?: string | null, fallback = "Not provided") =>
  value && value.trim() ? value : fallback;

export const getInteractionSliderValues = (personality?: string | null) => {
  const normalized = (personality ?? "").toLowerCase();
  const isExtrovert = normalized.includes("extrovert");
  const isIntrovert = normalized.includes("introvert");
  const hasGold = normalized.includes("gold");
  const hasOrange = normalized.includes("orange");
  const hasBlue = normalized.includes("blue");
  const hasGreen = normalized.includes("green");

  return {
    ie: isExtrovert ? 75 : isIntrovert ? 25 : 50,
    og: hasGold ? 75 : hasOrange ? 25 : 50,
    grb: hasBlue ? 75 : hasGreen ? 25 : 50,
  };
};

export const getBaseUrl = (): string => {
  // Vite exposes import.meta.env.BASE_URL; CRA exposes process.env.PUBLIC_URL
  const viteBase: string | undefined =
    typeof import.meta !== "undefined"
      ? (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL
      : undefined;
  const craBase: string | undefined =
    typeof process !== "undefined"
      ? (process as { env?: { PUBLIC_URL?: string } }).env?.PUBLIC_URL
      : undefined;
  const base = viteBase || craBase || "/";
  return base.endsWith("/") ? base : `${base}/`;
};

export const getStyleSlug = (styleValue: string): string => {
  const expanded = styleValue.trim().replace(/([a-z])([A-Z])/g, "$1 $2");
  const cleaned = expanded.toLowerCase();
  const normalized = cleaned.replace(/\+/g, " ").replace(/\s+/g, " ").trim();
  const override = STYLE_SLUG_OVERRIDES[normalized];
  if (override) {
    return override;
  }
  return normalized.replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, "-");
};

export const getStyleDescription = (styleValue: string): string => {
  const direct =
    STYLE_DESCRIPTIONS[styleValue] ??
    STYLE_DESCRIPTIONS[styleValue.replace(/\s+/g, "")];
  if (direct) {
    return direct;
  }
  const title = toTitleCase(styleValue);
  return STYLE_DESCRIPTIONS[title] ?? "";
};

export const getStyleImageCandidates = (
  styleValue: string,
  imageIndex: number
): string[] => {
  const slug = getStyleSlug(styleValue);
  const base = `${getBaseUrl()}style_images/${slug}/`;
  const exts = ["webp", "jpg", "jpeg", "png"];
  const maxFallbackIndex = 8;
  const primaryIndex = Number.isFinite(imageIndex) && imageIndex > 0 ? imageIndex : 1;
  const indices = [
    primaryIndex,
    ...Array.from({ length: maxFallbackIndex }, (_, idx) => idx + 1),
  ];
  const uniqueIndices = Array.from(new Set(indices));
  return uniqueIndices.flatMap((idx) =>
    exts.map((ext) => `${base}${idx}.${ext}`)
  );
};
